import React, { useContext, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import { t } from "i18next";

import PortalContext from "../../context/portalContext";
import DataContext from "../../context/dataContext";

import CustomInput from "../inputs";
import { useLoginUser } from "../../apiHooks/auth";
import { FlexCenter } from "../../styles";
import { brandLogoSm, closeSvg, errorSvg, linkArrowLeft } from "../../svgs";
import CustomGoogleAuth from "../customGoogleAuth";
import CustomFacebookAuth from "../customFacebookAuth";
import { ModalWrapper, AuthContent, AuthPrimaryBtn } from "./styled";
import ErrorNotification from "./notifications";

const LoginModal: React.FC<any> = () => {
  const modalWrapper = useRef(null);
  const [open, setOpen] = useState(false);
  const {
    authErrorObject: serverErrors,
    setAuthErrorObject,
    setAuthErrors,
  } = useContext(DataContext);
  const { closeAuthPortals, openRegister, openUpdatePassword } =
    useContext(PortalContext);

  const outsideClick = (e) => {
    return e.target === modalWrapper.current && closeAuthPortals();
  };

  useEffect(() => {
    setOpen(true);
    document.querySelector("body").classList.add("fixed");
    window.addEventListener("click", outsideClick);
    return () => {
      document.querySelector("body").classList.remove("fixed");
      window.removeEventListener("click", outsideClick);
      setAuthErrors([]);
    };
  }, []);

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm();

  // api hook
  const { loginUser, loading } = useLoginUser();

  const onSubmit = (data) => {
    setAuthErrorObject([]);
    loginUser({ params: { ...data, email: data.email.trim() } });
  };

  useEffect(() => {
    if (serverErrors.length && Object.keys(errors)?.length) {
      setAuthErrorObject([]);
    }
  }, [Object.keys(errors)?.length]);

  // reset server errors on input change
  useEffect(() => {
    const subscription = watch((value, { name, type }) => {
      if (serverErrors.length && value?.email) {
        setAuthErrorObject([]);
      }
    });
    return () => subscription.unsubscribe();
  }, [watch, serverErrors.length]);

  // console.log(Object.keys(errors).length, serverErrors);
  return (
    <ModalWrapper open={true} ref={modalWrapper}>
      <AuthContent>
        <button
          className="close"
          onClick={() => !loading && closeAuthPortals()}
          style={{ opacity: `${loading ? "0.5" : "1"}` }}
        >
          {closeSvg()}
          <FlexCenter>{linkArrowLeft()}</FlexCenter>
        </button>

        <div className="auth-content -login">
          {/* for mobile */}
          <Link to="/" role="none" className="brand-logo">
            {brandLogoSm()}
          </Link>

          <div className="heading">
            <span>გაიარე ავტორიზაცია</span>
            <span className="mobile">შოპინგი იწყება აქ</span>
          </div>

          <FlexCenter className="switch-auth">
            <button className="active">ავტორიზაცია </button>
            <button
              onClick={() => !loading && openRegister()}
              style={{ opacity: `${loading ? "0.5" : "1"}` }}
            >
              რეგისტრაცია
            </button>
          </FlexCenter>

          {/* ---  display error if multiple fields has error or server response --- */}
          {serverErrors?.find((el) => el.type === "email")?.msg ||
          Object.keys(errors).length > 1 ? (
            <ErrorNotification
              errorText={serverErrors?.find((el) => el.type === "email")?.msg}
            />
          ) : null}

          <form autoComplete="off" onSubmit={handleSubmit(onSubmit)}>
            {errors?.email && !errors?.password ? (
              <div className="error">
                {errorSvg()}
                <span>{errors.email?.message?.toString()}</span>
              </div>
            ) : null}
            <CustomInput
              margin="0 0 12px"
              inputType="email"
              id="sigin-email"
              inputName="მეილი"
              reactHookOptions={register("email", {
                required: {
                  value: true,
                  message: t("emailRequired"),
                },
                pattern: {
                  value:
                    /^(([^<>()[\]\\.,;:\s@"]+(\.[^<>()[\]\\.,;:\s@"]+)*)|(".+"))@((\[[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\.[0-9]{1,3}\])|(([a-zA-Z\-0-9]+\.)+[a-zA-Z]{2,}))$/,
                  message: t("emailNotValid"),
                },
              })}
              errors={errors.email}
              // errorMessages={<>{errors.email && <span>{errors.email.message.toString()}</span>} </>}
            />

            {!errors?.email && errors?.password ? (
              <div className="error">
                {errorSvg()}
                <span>{errors?.password?.message?.toString()}</span>
              </div>
            ) : null}
            <CustomInput
              margin="0 0 12px"
              inputType="password"
              id="signin-password"
              inputName="პაროლი"
              reactHookOptions={register("password", {
                required: {
                  value: true,
                  message: t("passwordRequired"),
                },
                minLength: {
                  value: 8,
                  message: t("passwordCharacters"),
                },
              })}
              errors={errors.password}
              // errorMessages={<>{errors.password && <span>{errors.password.message.toString()}</span>}</>}
            />

            <button
              type="button"
              onMouseUp={() => !loading && openUpdatePassword()}
              className="update-password"
              style={{ opacity: `${loading ? "0.5" : "1"}` }}
            >
              დაგავიწყდა პაროლი?
            </button>

            <AuthPrimaryBtn style={{ opacity: `${loading ? "0.5" : "1"}` }}>
              {" "}
              შესვლა
            </AuthPrimaryBtn>
          </form>

          <div className="divider">
            <span>ან</span>
          </div>

          <div
            className="alt-auth"
            style={{ opacity: `${loading ? "0.5" : "1"}` }}
          >
            <CustomFacebookAuth />
            <CustomGoogleAuth />
          </div>
        </div>
      </AuthContent>
    </ModalWrapper>
  );
};

export default LoginModal;
